import axios from "./axios";
import { resolveDashboardScope } from "./index";

export type SearchSource = "classic" | "sales_nav";

export type ProspectTarget = {
  locations: string[];
  jobTitles: string[];
  industries: string[];
  companySize: string[];
  keywords: string[];
  companies: string[];
  seniority?: string[];
  functions?: string[];
  suggested?: Record<string, string[]>;
  unsupported?: string[];
  rawDescription?: string;
};

export type ProspectSearchJob = {
  id: string;
  status: string;
  desiredCount: number;
  discovered: number;
  processed: number;
  duplicates: number;
  failed: number;
  addedToListCount?: number;
  message: string;
  error: string;
  accountDisconnected?: boolean;
  linkedinUrl?: string;
  searchSource?: SearchSource;
  savedListId?: string | null;
  stagingListId?: string | null;
  ownerId?: string | null;
  target?: ProspectTarget;
  targetSummary?: string;
  accountName?: string;
  createdAt?: string | null;
  completedAt?: string | null;
};

export type SavedAudience = {
  id: string;
  name: string;
  description?: string;
  target: ProspectTarget;
  searchSource?: SearchSource;
  ownerId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type DiscoveredProspect = {
  id: string;
  fullName: string;
  title: string;
  company: string;
  location: string;
  connected: string;
  email: string;
  phone: string;
  imageUrl: string;
  linkedinUrl: string;
  inList: boolean;
};

const scopeBody = (userId?: string, workspaceId?: string, ownerId?: string) => {
  const { resolvedUserId, resolvedWorkspaceId } = resolveDashboardScope(
    userId,
    workspaceId
  );
  return {
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
    ownerId: ownerId || undefined,
  };
};

export async function parseProspectTarget(payload: {
  description: string;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/parseProspectTarget", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    description: payload.description,
  });
  return response.data as { target: ProspectTarget };
}

export async function detectSalesNavAccess(payload: {
  ownerId: string;
  userId?: string;
  workspaceId?: string;
}) {
  const response = await axios.post("/prospect/detectSalesNavAccess", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
  });
  return response.data as {
    available: boolean;
    reason?: string;
    ownerName?: string;
  };
}

export async function createProspectSearch(payload: {
  target: ProspectTarget;
  ownerId: string;
  desiredCount: number;
  searchSource?: SearchSource;
  savedAudienceId?: string;
  userId?: string;
  workspaceId?: string;
}) {
  const response = await axios.post("/prospect/createProspectSearch", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    target: payload.target,
    desiredCount: payload.desiredCount,
    searchSource: payload.searchSource || "classic",
    savedAudienceId: payload.savedAudienceId,
  });
  return response.data as { job: ProspectSearchJob };
}

export async function startProspectSearch(payload: {
  jobId: string;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/startProspectSearch", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    jobId: payload.jobId,
  });
  return response.data;
}

export async function getProspectSearch(payload: {
  jobId: string;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/getProspectSearch", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    jobId: payload.jobId,
  });
  return response.data as { job: ProspectSearchJob };
}

export async function listProspectSearches(payload: {
  page?: number;
  limit?: number;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/listProspectSearches", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    page: payload.page || 1,
    limit: payload.limit || 25,
  });
  return response.data as {
    jobs: ProspectSearchJob[];
    total: number;
    page: number;
    limit: number;
  };
}

export async function getProspectSearchResults(payload: {
  jobId: string;
  page?: number;
  limit?: number;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/getProspectSearchResults", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    jobId: payload.jobId,
    page: payload.page || 1,
    limit: payload.limit || 50,
  });
  return response.data as {
    job: ProspectSearchJob;
    prospects: DiscoveredProspect[];
    total: number;
    page: number;
    limit: number;
  };
}

export async function cancelProspectSearch(payload: {
  jobId: string;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/cancelProspectSearch", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    jobId: payload.jobId,
  });
  return response.data as { job: ProspectSearchJob };
}

export async function addProspectSearchToList(payload: {
  jobId: string;
  listId?: string;
  prospectIds?: string[];
  createList?: { listName: string; description?: string };
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/addProspectSearchToList", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    jobId: payload.jobId,
    listId: payload.listId,
    prospectIds: payload.prospectIds,
    createList: payload.createList,
  });
  return response.data as {
    listId: string;
    added: number;
    job: ProspectSearchJob;
  };
}

export async function listSavedAudiences(payload: {
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/listSavedAudiences", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
  });
  return response.data as { audiences: SavedAudience[] };
}

export async function createSavedAudience(payload: {
  name: string;
  description?: string;
  target: ProspectTarget;
  searchSource?: SearchSource;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/createSavedAudience", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    name: payload.name,
    description: payload.description,
    target: payload.target,
    searchSource: payload.searchSource || "classic",
  });
  return response.data as { audience: SavedAudience };
}

export async function updateSavedAudience(payload: {
  id: string;
  name?: string;
  description?: string;
  target?: ProspectTarget;
  searchSource?: SearchSource;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/updateSavedAudience", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    id: payload.id,
    name: payload.name,
    description: payload.description,
    target: payload.target,
    searchSource: payload.searchSource,
  });
  return response.data as { audience: SavedAudience };
}

export async function duplicateSavedAudience(payload: {
  id: string;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/duplicateSavedAudience", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    id: payload.id,
  });
  return response.data as { audience: SavedAudience };
}

export async function deleteSavedAudience(payload: {
  id: string;
  userId?: string;
  workspaceId?: string;
  ownerId?: string;
}) {
  const response = await axios.post("/prospect/deleteSavedAudience", {
    ...scopeBody(payload.userId, payload.workspaceId, payload.ownerId),
    id: payload.id,
  });
  return response.data as { ok: boolean };
}

export const emptyTarget = (): ProspectTarget => ({
  locations: [],
  jobTitles: [],
  industries: [],
  companySize: [],
  keywords: [],
  companies: [],
  seniority: [],
  functions: [],
  suggested: {},
  unsupported: [],
  rawDescription: "",
});
